import type { RuntimeConfig } from '@/shared/config'
import { createHttpClient, createQueryClient } from '@/shared/api'
import {
  HttpAuthProvider,
  createAuthApi,
  createRenewLock,
  createSessionRenewer,
  createTokenStore,
} from '@/shared/auth'
import { resolveLocale, useLocaleStore } from '@/shared/i18n'
import { getEnabledModules } from '@/shared/modules'
import { createRealtimeClient } from '@/shared/realtime'
import type { AppServices } from '@/shared/services'
import { moduleRegistry } from '../modules/registry'

/** Composition root for application services. The only place that picks implementations. */
export function createServices(config: RuntimeConfig): AppServices {
  const tokens = createTokenStore()
  const http = createHttpClient({
    baseUrl: config.apiBaseUrl,
    getLanguage: () => resolveLocale(useLocaleStore.getState().locale, config.locale),
    // Invoked only after construction, so `renewer` and `auth` are initialised by then.
    auth: {
      getAccessToken: () => tokens.getAccessToken(),
      renew: () => renewer.renew(),
      onSessionEnded: () => {
        auth.notifyExpired()
      },
    },
  })
  const authApi = createAuthApi(http)
  const renewer = createSessionRenewer({
    tokens,
    exchange: (refreshToken) => authApi.refresh({ refresh_token: refreshToken }),
    runExclusive: createRenewLock(),
  })
  const auth = new HttpAuthProvider(authApi, tokens)

  return {
    config,
    http,
    auth,
    realtime: createRealtimeClient(config),
    queryClient: createQueryClient(),
    modules: getEnabledModules(moduleRegistry, config),
  }
}
